/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect } from 'react'
import Modal from '../../../../components/Modal'
import { useTranslation } from 'react-i18next'
import type { Task } from '../../../../services/api/types'
import { useNextTask } from './useNextTask'

interface NextTaskModalProps {
  nextTask: Task
  onClose?: () => void
}

interface TaskFile {
  id: number
  name: string
  type: string
}

/**
 * Modal shown after completing a task to ask if user wants to go to next task
 * Migrated from: lego/page/tasks/next-task/Modal.jsx
 */
export default function NextTaskModal({ nextTask, onClose }: NextTaskModalProps) {
  const { t } = useTranslation()
  const { goToNextTask } = useNextTask()
  const [taskFile, setTaskFile] = useState<TaskFile | null>(null)

  useEffect(() => {
    // Extract first file from task
    if (nextTask?.files && nextTask.files.length === 1) {
      const file = nextTask.files[0].draftFsi || nextTask.files[0].targetFsi || nextTask.files[0].sourceFsi || null
      setTaskFile(file)
    }
  }, [nextTask])

  const handleConfirm = () => {
    goToNextTask(nextTask)
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal show={true} handleClose={onClose || (() => {})} title={t('TASK_COMPLETE_NEXT_TASK_MODAL_TITLE')} size="md">
      <Modal.Body>
        <p>{t('TASK_COMPLETE_NEXT_TASK_MODAL_TEXT')}</p>
        <div>
          <b>{t('TASK_NAME_LABEL')}:</b> {nextTask.name}
          <br />
          <b>{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}:</b>{' '}
          {(nextTask as any).workflowFormattedId || (nextTask.workflow as any)?.formattedId}
          <br />
          <div className="word-break">
            <b>{t('DOCUMENT(S)')}: </b>
            {nextTask.files && nextTask.files.length > 1 && <span>{t('WORKFLOW_MULTIPLE_DOCS')}</span>}

            {nextTask.files && nextTask.files.length === 0 && (
              <span title={(nextTask as any).filename} className="whitespaceprewrap">
                {(nextTask as any).filename || t('NOT_AVAILABLE')}
              </span>
            )}

            {nextTask.files && nextTask.files.length === 1 && taskFile && (
              <span title={taskFile.name}>{taskFile.name}</span>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link text-capitalize" onClick={onClose} data-action="cancel" data-test-id="cancel">
          {t('NO')}
        </button>
        <button
          className="btn btn-primary text-capitalize"
          onClick={handleConfirm}
          id="a9ae6a6f-381e-42db-b47f-0754895f8756"
          data-action="confirm"
        >
          {t('YES')}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
